import {
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  styled,
} from "@mui/material";
import { ArrowForwardIos, Padding } from "@mui/icons-material";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { FC } from "react";
import { menus } from "../../constants";
import { Blog } from "../../interfaces";
import { formatDateMY } from "../../utils/date";

const BlogPosts: FC<Props> = ({ blogs }) => {
  const router = useRouter();

  const getLocalizedLink = (basePath: string) => {
    switch (router.locale) {
      case "ms":
        return `https://donate.sols.foundation/ms${basePath}`;
      case "cn":
        return `https://donate.sols.foundation${basePath}`;
      case "id":
        return `https://donate.sols.foundation${basePath}`;
      default:
        return `https://donate.sols.foundation${basePath}`;
    }
  };

  // Generate links for different resources
  const energyAccessLink = getLocalizedLink("/energy-access");
  const solarAcademyLink = getLocalizedLink(
    "/campaign/food-accommodation-for-solar-academy-students"
  );
  const freeEducationLink = getLocalizedLink(
    "/campaign/free-education-for-b40-communities"
  );

  const strings = {
    donate_now: "Donate Now",
    join_us: "Join Us",
    join_a_class: "Join A Class",
    blog_posts: "Blog Posts",
  };

  const donate_to = [
    {
      name: "Fund Kampung Solar Projects",
      href: energyAccessLink,
    },
    {
      name: "Support Solar Academy Students",
      href: solarAcademyLink,
    },
    {
      name: "Offer Free Education for B40",
      href: freeEducationLink,
    },
  ];

  return (
    <aside style={{ marginLeft: "-15px" }}>
      <List
        subheader={
          <StyledListSubheader>{strings.donate_now}</StyledListSubheader>
        }
        sx={{ marginTop: "-2rem" }}
      >
        {donate_to?.map((link, index) => (
          <Link href={link.href} key={`aside-${link.name}`}>
            <StyledListItem>
              <ListItemIcon className="listItemIcon">
                <ArrowForwardIos className="icon" />
              </ListItemIcon>
              <ListItemText
                classes={{ primary: "listItemPrimary" }}
                primary={link.name}
              />
            </StyledListItem>
            {index !== donate_to.length - 1 && <Divider />}
          </Link>
        ))}
      </List>

      <List
        subheader={<StyledListSubheader>{strings.join_us}</StyledListSubheader>}
      >
        {menus(router)[3].links?.map((link, index) => (
          <Link href={link.href} key={`aside-${link.name}`}>
            <StyledListItem>
              <ListItemIcon className="listItemIcon">
                <ArrowForwardIos className="icon" />
              </ListItemIcon>
              <ListItemText
                classes={{ primary: "listItemPrimary" }}
                primary={link.name}
              />
            </StyledListItem>
            {index !== menus(router)[3].links.length - 1 && <Divider />}
          </Link>
        ))}
      </List>

      <List
        subheader={
          <StyledListSubheader>{strings.join_a_class}</StyledListSubheader>
        }
      >
        {menus(router)[0].links?.map((link, index) => (
          <Link href={link.href} key={`aside-${link.name}`}>
            <StyledListItem>
              <ListItemIcon className="listItemIcon">
                <ArrowForwardIos className="icon" />
              </ListItemIcon>
              <ListItemText
                classes={{ primary: "listItemPrimary" }}
                primary={link.name}
              />
            </StyledListItem>
            {index !== menus(router)[0].links.length - 1 && <Divider />}
          </Link>
        ))}
      </List>

      <List
        subheader={
          <StyledListSubheader>{strings.blog_posts}</StyledListSubheader>
        }
      >
        {blogs?.map((blog) => (
          <Link href={`/blog/${blog.postId}`} key={`aside-${blog.postTitle}`}>
            <StyledListItem>
              <ListItemAvatar>
                <Avatar src={blog.postImage} alt={blog.postTitle} />
              </ListItemAvatar>
              <ListItemText
                classes={{
                  primary: "listItemPrimary",
                  secondary: "listItemSecondary",
                }}
                primary={blog.postTitle}
                secondary={formatDateMY(blog.postDateTs)}
              />
            </StyledListItem>
          </Link>
        ))}
      </List>
    </aside>
  );
};

interface Props {
  blogs: Blog[];
}

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 600,
  color: theme.palette.primary.dark,
  marginTop: "2rem",
  fontFamily: '"Open Sans", Helvetica, Arial, Verdana, sans-serif',
  padding: "0 5px !important",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  padding: "10px 5px !important",
  ["& .listItemIcon"]: {
    minWidth: "30px!important",
    ["& .icon"]: {
      fontSize: "0.8rem",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
  },
  ["& .listItemPrimary"]: {
    color: theme.palette.primary.dark,
    fontSize: "0.9rem",
    transition: "all 0.2s ease-in-out",
    fontFamily: '"Open Sans", Helvetica, Arial, Verdana, sans-serif',
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  ["& .listItemSecondary"]: {
    fontSize: "0.7rem!important",
    color: "#777777!important",
  },
}));

export default BlogPosts;
